/* eslint-disable */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  ChevronRight, ChevronLeft, Users, Building2, Plus, X, Search, UserX,
  Edit2, UserPlus, Loader2, AlertCircle, BookOpen, ClipboardList,
  MoreVertical,
} from 'lucide-react';
import { useSelector } from 'react-redux';
import Page from '../../../components/Page';
import * as deptSvc from '../../../services/clientAdmin/departmentServices';
import tenantUserSvc from '../../../services/clientAdmin/tenantUserServices';

// ── Helpers ──────────────────────────────────────────────────────────────────

function fmt(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

function ErrorBanner({ message, onRetry }) {
  return (
    <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
      <AlertCircle size={15} className="flex-shrink-0" />
      <span className="flex-1">{message}</span>
      {onRetry && (
        <button onClick={onRetry} className="underline text-red-600 hover:text-red-800 flex-shrink-0">Retry</button>
      )}
    </div>
  );
}

function SkeletonRow() {
  return (
    <div className="p-4 border-b border-gray-100 animate-pulse">
      <div className="h-4 bg-gray-200 rounded w-40 mb-2" />
      <div className="flex gap-4"><div className="h-3 bg-gray-200 rounded w-16" /><div className="h-3 bg-gray-200 rounded w-20" /></div>
    </div>
  );
}

// ── Team row overflow menu ────────────────────────────────────────────────────

function TeamMenu({ team, onEdit, onToggle }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    function h(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false); }
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  return (
    <div className="relative" ref={ref}>
      <button onClick={() => setOpen(o => !o)}
        className="w-7 h-7 flex items-center justify-center rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors">
        <MoreVertical size={15} />
      </button>
      {open && (
        // FIX 2: Use fixed positioning to avoid being clipped by overflow-x:auto on the table wrapper
        <div
          className="fixed z-[9999] bg-white border border-gray-200 rounded-lg shadow-lg py-1 w-36"
          style={{
            top: ref.current ? ref.current.getBoundingClientRect().bottom + 4 : 0,
            left: ref.current ? ref.current.getBoundingClientRect().right - 144 : 0,
          }}
        >
          <button onClick={() => { onEdit(); setOpen(false); }}
            className="flex items-center gap-2 w-full px-3 py-2 text-sm text-gray-700 hover:bg-gray-50">
            <Edit2 size={13} /> Edit
          </button>
          <button onClick={() => { onToggle(); setOpen(false); }}
            className={`flex items-center gap-2 w-full px-3 py-2 text-sm hover:bg-gray-50 ${team.isActive ? 'text-red-600' : 'text-green-600'
              }`}>
            {team.isActive ? '× Deactivate' : '✓ Activate'}
          </button>
        </div>
      )}
    </div>
  );
}

// ── Modal: Dept form ─────────────────────────────────────────────────────────

function DeptModal({ mode, initial, onClose, onSave, departments }) {
  const [form, setForm] = useState({ name: initial?.name || '', description: initial?.description || '', headUserId: String(initial?.headUserId || initial?.headuserid || '') });
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [admins, setAdmins] = useState([]);
  const clientId = useSelector((state) => state.userInfo?.clientId);
  const userRole = useSelector((state) => state.userInfo?.role);

  useEffect(() => {
    if (userRole === 'MANAGER') return;
    tenantUserSvc.getTenantUsers(clientId, 0, 200, '', 'MANAGER').then(res => {
      if (res.ok) setAdmins(res.data?.users || res.data || []);
    });
  }, []);

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.name.trim()) { setError('Name is required'); return; }
    const isDuplicate = departments.some(d =>
      d.name.trim().toLowerCase() === form.name.trim().toLowerCase() &&
      d.id !== initial?.id   
    );
    if (isDuplicate) { setError('A department with this name already exists.'); return; }

    setSaving(true);
    try { await onSave({ ...form, headUserId: form.headUserId ? Number(form.headUserId) : null }); onClose(); }
    catch (err) { setError(err?.data?.message || err?.message || 'Failed to save'); }
    finally { setSaving(false); }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-xl w-full max-w-md mx-4 p-6">
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-lg font-semibold text-gray-900">{mode === 'create' ? 'New Department' : 'Edit Department'}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Name <span className="text-red-500">*</span></label>
            <input type="text" value={form.name}
              onChange={e => { setForm({ ...form, name: e.target.value }); setError(''); }}
              placeholder="e.g. NEET Batch 2026"
              className={`w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-400 ${error ? 'border-red-400' : 'border-gray-300'}`} />
            {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description <span className="text-gray-400 font-normal">(optional)</span></label>
            <textarea rows={3} value={form.description}
              onChange={e => setForm({ ...form, description: e.target.value })}
              placeholder="Optional description"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-400 resize-none" />
          </div>
          {userRole !== 'MANAGER' && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Department Head (Manager) <span className="text-red-500">*</span></label>
            <select value={form.headUserId}
              onChange={e => setForm({ ...form, headUserId: e.target.value })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-400 bg-white">
              <option value="">Select Department Head</option>
              {admins.map(u => (
                <option key={u.id} value={u.id}>{u.firstName} {u.lastName} ({u.email})</option>
              ))}
            </select>
          </div>
          )}
          <div className="flex justify-end gap-3 pt-1">
            <button type="button" onClick={onClose}
              className="px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
            <button type="submit" disabled={saving}
              className="px-4 py-2 text-sm font-medium bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-60 flex items-center gap-2">
              {saving && <Loader2 size={14} className="animate-spin" />}
              {mode === 'create' ? 'Create Department' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Modal: Team form ─────────────────────────────────────────────────────────

function TeamModal({ mode, initial, onClose, onSave, teams }) {
  const [name, setName] = useState(initial?.name || '');
  const [leaderUserId, setLeaderUserId] = useState(String(initial?.leaderUserId || initial?.leaderuserid || ''));
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [admins, setAdmins] = useState([]);
  const clientId = useSelector((state) => state.userInfo?.clientId);

  useEffect(() => {
    tenantUserSvc.getTenantUsers(clientId, 0, 200, '', 'MANAGER').then(res => {
      if (res.ok) {
        const list = res.data?.users || res.data || [];
        // Ensure the current leader appears in the dropdown even if not in the fetched list
        const currentId = initial?.leaderUserId || initial?.leaderuserid;
        if (currentId && !list.some(u => u.id === currentId)) {
          list.unshift({ id: currentId, firstName: initial?.leaderFirstName || initial?.leaderfirstname || '', lastName: initial?.leaderLastName || initial?.leaderlastname || '', email: '' });
        }
        setAdmins(list);
      }
    });
  }, []);

  async function handleSubmit(e) {
    e.preventDefault();
    if (!name.trim()) { setError('Team name is required'); return; }
     const isDuplicate = teams.some(t =>
      t.name.trim().toLowerCase() === name.trim().toLowerCase() &&
      t.id !== initial?.id   
    );
    if (isDuplicate) { setError('A team with this name already exists in this department.'); return; }

    setSaving(true);
    try { await onSave({ name: name.trim(), leaderUserId: leaderUserId ? Number(leaderUserId) : null }); onClose(); }
    catch (err) { setError(err?.data?.message || err?.message || 'Failed to save'); }
    finally { setSaving(false); }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-xl w-full max-w-md mx-4 p-6">
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-lg font-semibold text-gray-900">{mode === 'create' ? 'New Team' : 'Edit Team'}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Team Name <span className="text-red-500">*</span></label>
            <input type="text" value={name}
              onChange={e => { setName(e.target.value); setError(''); }}
              placeholder="e.g. NEET Section 1"
              className={`w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-400 ${error ? 'border-red-400' : 'border-gray-300'}`} />
            {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Team Leader <span className="text-gray-400 font-normal">(optional)</span></label>
            <select value={leaderUserId}
              onChange={e => setLeaderUserId(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-400 bg-white">
              <option value="">Select Team Leader</option>
              {admins.map(u => (
                <option key={u.id} value={u.id}>{u.firstName} {u.lastName} ({u.email})</option>
              ))}
            </select>
          </div>
          <div className="flex justify-end gap-3 pt-1">
            <button type="button" onClick={onClose}
              className="px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
            <button type="submit" disabled={saving}
              className="px-4 py-2 text-sm font-medium bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-60 flex items-center gap-2">
              {saving && <Loader2 size={14} className="animate-spin" />}
              {mode === 'create' ? 'Create Team' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── View Team Drawer ──────────────────────────────────────────────────────────

function ViewTeamDrawer({ team, deptName, onClose, onMemberRemoved }) {
  const [members, setMembers] = useState([]);
  const [content, setContent] = useState([]);
  const [loadingM, setLoadingM] = useState(true);
  const [loadingC, setLoadingC] = useState(true);
  const [removing, setRemoving] = useState(null);
  const [errorM, setErrorM] = useState('');
  const [errorC, setErrorC] = useState('');

  useEffect(() => {
    deptSvc.getTeamMembers(team.id)
      .then(r => {
        if (r.ok) setMembers(r.data || []);
        else setErrorM('Failed to load members');
      })
      .catch(() => setErrorM('Failed to load members'))
      .finally(() => setLoadingM(false));

    deptSvc.getTeamContent(team.id)
      .then(r => {
        if (r.ok) setContent(r.data || []);
        else setErrorC('Failed to load content');
      })
      .catch(() => setErrorC('Failed to load content'))
      .finally(() => setLoadingC(false));
  }, [team.id]);

  async function handleRemove(userId) {
    setRemoving(userId);
    const r = await deptSvc.removeLearner(team.id, userId);
    if (r.ok) {
      setMembers(prev => prev.filter(m => m.id !== userId));
      onMemberRemoved && onMemberRemoved(team.id);
    }
    setRemoving(null);
  }

  return (
    <>
      <div className="fixed top-[65px] inset-x-0 bottom-0 bg-black/40 z-40" onClick={onClose} />
      <div className="fixed top-[65px] right-0 bottom-0 z-50 w-full sm:w-[380px] bg-white shadow-2xl flex flex-col">

        {/* Header */}
        <div className="flex items-start justify-between px-5 py-4 border-b border-gray-200 flex-shrink-0">
          <div className="min-w-0 flex-1">
            <h3 className="text-base font-semibold text-gray-900 truncate">{team.name}</h3>
            <p className="text-sm text-gray-400 mt-0.5 truncate">{deptName}</p>
          </div>
          <button onClick={onClose} className="p-1 rounded-lg hover:bg-gray-100 flex-shrink-0 ml-3">
            <X size={18} className="text-gray-500" />
          </button>
        </div>

        {/* Single scrollable body */}
        <div className="flex-1 overflow-y-auto">

          {/* ── LEARNERS section ── */}
          <div>
            <div className="flex items-center gap-2 px-5 py-3 border-b border-gray-100 bg-gray-50/70">
              <Users size={14} className="text-gray-400" />
              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                Learners ({loadingM ? '…' : members.length})
              </span>
            </div>

            {loadingM ? (
              <div className="flex items-center justify-center py-8 text-gray-400">
                <Loader2 size={18} className="animate-spin mr-2" /> Loading…
              </div>
            ) : errorM ? (
              <div className="p-4"><ErrorBanner message={errorM} /></div>
            ) : members.length === 0 ? (
              <div className="px-5 py-6 text-center text-sm text-gray-400">
                No learners in this team yet.
              </div>
            ) : (
              <div className="divide-y divide-gray-50">
                {members.map(m => (
                  <div key={m.id} className="flex items-center justify-between px-5 py-3 hover:bg-gray-50">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-gray-900 truncate">{m.firstName} {m.lastName}</p>
                      <p className="text-xs text-gray-400 truncate">{m.email}</p>
                    </div>
                    <button onClick={() => handleRemove(m.id)} disabled={removing === m.id}
                      className="flex-shrink-0 ml-3 text-xs text-red-500 hover:text-red-700 border border-red-200 hover:bg-red-50 rounded px-2 py-1 disabled:opacity-50 transition-colors">
                      {removing === m.id ? <Loader2 size={11} className="animate-spin" /> : 'Remove'}
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* ── ASSIGNED CONTENT section ── */}
          <div className="border-t border-gray-200">
            <div className="flex items-center gap-2 px-5 py-3 border-b border-gray-100 bg-gray-50/70">
              <BookOpen size={14} className="text-gray-400" />
              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                Assigned Content ({loadingC ? '…' : content.length})
              </span>
            </div>

            {loadingC ? (
              <div className="flex items-center justify-center py-8 text-gray-400">
                <Loader2 size={18} className="animate-spin mr-2" /> Loading…
              </div>
            ) : errorC ? (
              <div className="p-4"><ErrorBanner message={errorC} /></div>
            ) : content.length === 0 ? (
              <div className="px-5 py-6 text-center text-sm text-gray-400">
                No content assigned yet.
              </div>
            ) : (
              <div className="divide-y divide-gray-50">
                {content.map(c => (
                  <div key={c.sccpId} className="flex items-start gap-3 px-5 py-3 hover:bg-gray-50">
                    {c.type === 'assessment'
                      ? <ClipboardList size={14} className="text-purple-500 flex-shrink-0 mt-0.5" />
                      : <BookOpen size={14} className="text-blue-500 flex-shrink-0 mt-0.5" />}
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-gray-800 truncate">{c.title}</p>
                      <p className="text-xs text-gray-400 mt-0.5">
                        {c.type === 'assessment' ? 'Assessment' : 'Course'} · {fmt(c.assignedAt)} · via dept
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="px-5 py-3 text-xs text-gray-400 border-t border-gray-100">
              To unassign content, go to the{' '}
              <a href="/app/catalog" className="text-purple-600 hover:underline">Catalog page</a>.
            </div>
          </div>

        </div>
      </div>
    </>
  );
}

// ── Modal: Add Learners ──────────────────────────────────────────────────────

function AddLearnersModal({ team, onClose, onAdded }) {
  const [search, setSearch] = useState('');
  const [activeTab, setActiveTab] = useState(true);
  const [learners, setLearners] = useState([]);
  const [selected, setSelected] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const searchTimer = useRef(null);

  const loadLearners = useCallback(async (q, active) => {
    setLoading(true); setError('');
    const r = await deptSvc.getAvailableLearners(team.id, q, active);
    if (r.ok) setLearners(r.data);
    else setError('Failed to load learners');
    setLoading(false);
  }, [team.id]);

  useEffect(() => { loadLearners('', activeTab); }, [loadLearners, activeTab]);

  function handleSearchChange(e) {
    const q = e.target.value; setSearch(q);
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => loadLearners(q, activeTab), 300);
  }

  function handleTabChange(val) {
    setActiveTab(val);
    setSearch('');
    setSelected([]);
  }

  function toggle(id) {
    setSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  }

  async function handleAdd() {
    if (selected.length === 0) return;
    setSaving(true);
    const r = await deptSvc.addLearners(team.id, selected);
    if (r.ok) { onAdded && onAdded(team.id, selected.length); onClose(); }
    else setError(r.data?.message || 'Failed to add learners');
    setSaving(false);
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-xl w-full max-w-lg mx-4 flex flex-col" style={{ maxHeight: '80vh' }}>
        
        {/* Header */}
        <div className="p-6 border-b border-gray-100">
          <div className="flex items-center justify-between mb-1">
            <h2 className="text-lg font-semibold text-gray-900">Add Learners</h2>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
          </div>
          <p className="text-sm text-gray-500">Adding to <span className="font-medium">{team.name}</span></p>

          {/* Tabs */}
          <div className="flex gap-1 mt-3 border border-gray-200 rounded-lg p-0.5 bg-gray-50">
            <button
              onClick={() => handleTabChange(true)}
              className={`flex-1 py-1.5 text-sm rounded-md font-medium transition-colors ${
                activeTab ? 'bg-white text-purple-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Active
            </button>
            <button
              onClick={() => handleTabChange(false)}
              className={`flex-1 py-1.5 text-sm rounded-md font-medium transition-colors ${
                !activeTab ? 'bg-white text-purple-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Inactive
            </button>
          </div>

          {/* Search */}
          <div className="relative mt-3">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={handleSearchChange}
              placeholder={`Search ${activeTab ? 'active' : 'inactive'} learners...`}
              className="w-full pl-8 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-400"
            />
          </div>
        </div>

        {/* List */}
        <div className="flex-1 overflow-y-auto">
          {error && <div className="p-4"><ErrorBanner message={error} /></div>}
          {loading ? (
            <div className="flex items-center justify-center py-10 text-gray-400">
              <Loader2 size={20} className="animate-spin mr-2" /> Searching…
            </div>
          ) : learners.length === 0 ? (
            <div className="text-center py-10 text-gray-400 text-sm">
              {search
                ? 'No learners match your search.'
                : `No ${activeTab ? 'active' : 'inactive'} learners available to add.`}
            </div>
          ) : (
            <>
              <div className="flex items-center px-5 py-2 border-b border-gray-100 text-xs font-semibold text-gray-400 uppercase tracking-wider">
                <div className="w-5 mr-3" /><div className="flex-1">NAME</div><div className="w-32">CURRENT DEPT</div>
              </div>
              {learners.map(l => (
                <label key={l.id} className="flex items-center px-5 py-3 hover:bg-gray-50 cursor-pointer border-b border-gray-50">
                  <input
                    type="checkbox"
                    checked={selected.includes(l.id)}
                    onChange={() => toggle(l.id)}
                    className="w-4 h-4 rounded border-gray-300 text-purple-600 focus:ring-purple-400 mr-3 flex-shrink-0"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{l.firstName} {l.lastName}</p>
                    <p className="text-xs text-gray-400 truncate">{l.email}</p>
                  </div>
                  <div className="w-32 text-xs text-gray-500 text-right flex-shrink-0">
                    {l.currentDept || <span className="text-gray-300">Unassigned</span>}
                  </div>
                </label>
              ))}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-between">
          <p className="text-sm text-gray-500">
            {selected.length === 0 ? 'Select learners to add' : `${selected.length} learner(s) selected`}
          </p>
          <div className="flex gap-3">
            <button onClick={onClose}
              className="px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
            <button onClick={handleAdd} disabled={selected.length === 0 || saving}
              className="px-4 py-2 text-sm font-medium bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 flex items-center gap-2">
              {saving && <Loader2 size={14} className="animate-spin" />}
              Continue →
            </button>
          </div>
        </div>

      </div>
    </div>
  );
}

// ── Main Component ───────────────────────────────────────────────────────────

export default function DepartmentsAndTeams() {
  const [departments, setDepartments] = useState([]);
  const [teams, setTeams] = useState([]);
  const [selectedDeptId, setSelectedDeptId] = useState(null);
  const [loadingDepts, setLoadingDepts] = useState(true);
  const [loadingTeams, setLoadingTeams] = useState(false);
  const [deptsError, setDeptsError] = useState('');
  const [teamsError, setTeamsError] = useState('');
  const userRole = useSelector((state) => state.userInfo?.role);
  const [search, setSearch] = useState('');
  const [mobileView, setMobileView] = useState('departments');

  const [deptModal, setDeptModal] = useState(null);
  const [teamModal, setTeamModal] = useState(null);
  const [viewDrawer, setViewDrawer] = useState(null);
  const [addModal, setAddModal] = useState(null);

  const loadDepartments = useCallback(async () => {
    setLoadingDepts(true); setDeptsError('');
    const r = await deptSvc.getDepartments();
    if (r.ok) {
      const data = r.data?.departments || r.data || [];
      setDepartments(data);

    } else {
      setDeptsError(r.data?.message || 'Failed to load departments');
    }
    setLoadingDepts(false);
  }, []);

  useEffect(() => { loadDepartments(); }, [loadDepartments]);

  const loadTeams = useCallback(async (deptId) => {
    if (!deptId) return;
    setLoadingTeams(true); setTeamsError('');
    const r = await deptSvc.getTeams(deptId);
    if (r.ok) setTeams(r.data?.teams || r.data || []);
    else setTeamsError(r.data?.message || 'Failed to load teams');
    setLoadingTeams(false);
  }, []);

  useEffect(() => { if (selectedDeptId) loadTeams(selectedDeptId); }, [selectedDeptId, loadTeams]);

  const selectedDept = departments.find(d => d.id === selectedDeptId);
  const filteredDepts = departments.filter(d =>
    d.name.toLowerCase().includes(search.toLowerCase())
  );

  async function handleDeptSave(form) {
    if (deptModal.mode === 'create') {
      const r = await deptSvc.createDepartment(form);
      if (!r.ok) throw r;
      await loadDepartments();
      setSelectedDeptId(r.data.id);
    } else {
      const r = await deptSvc.updateDepartment(deptModal.dept.id, form);
      if (!r.ok) throw r;
      setDepartments(prev => prev.map(d => d.id === deptModal.dept.id ? { ...d, ...r.data } : d));
    }
  }

  async function handleToggleDept(dept, e) {
    e.stopPropagation();
    const newActive = !dept.isActive;
    const r = await deptSvc.toggleDepartment(dept.id, newActive);
    if (r.ok) setDepartments(prev => prev.map(d => d.id === dept.id ? { ...d, isActive: newActive } : d));
  }

  async function handleTeamSave(form) {
    if (teamModal.mode === 'create') {
      const r = await deptSvc.createTeam(selectedDeptId, form);
      if (!r.ok) throw r;
      setTeams(prev => [...prev, { ...r.data, memberCount: 0 }]);
      setDepartments(prev => prev.map(d =>
        d.id === selectedDeptId ? { ...d, teamCount: (Number(d.teamCount) || 0) + 1 } : d
      ));
    } else {
      const r = await deptSvc.updateTeam(teamModal.team.id, form);
      if (!r.ok) throw r;
      setTeams(prev => prev.map(t => t.id === teamModal.team.id ? { ...t, name: r.data.name } : t));
    }
  }

  async function handleToggleTeam(team) {
    const newActive = !team.isActive;
    const r = await deptSvc.toggleTeam(team.id, newActive);
    if (r.ok) setTeams(prev => prev.map(t => t.id === team.id ? { ...t, isActive: newActive } : t));
  }

  function handleLearnersAdded(teamId, count) {
    setTeams(prev => prev.map(t =>
      t.id === teamId ? { ...t, memberCount: Number(t.memberCount) + count } : t
    ));

    loadDepartments();
  }

  function handleMemberRemoved(teamId) {
    setTeams(prev => prev.map(t =>
      t.id === teamId ? { ...t, memberCount: Math.max(0, Number(t.memberCount) - 1) } : t
    ));
    loadDepartments();
  }


  return (
    <Page title="Departments & Teams">
      <div className="p-4 sm:p-6 bg-gray-50 min-h-screen">

        {/* Header */}
        <div className="flex items-center justify-between mb-4 sm:mb-6">
          <div>
            <h1 className="text-lg sm:text-xl font-semibold text-gray-900">Departments &amp; Teams</h1>
            <p className="text-sm text-gray-500">Manage your organisation structure</p>
          </div>
          {userRole !== 'MANAGER' && (
          <button onClick={() => setDeptModal({ mode: 'create' })}
            className="flex items-center gap-2 bg-purple-600 text-white px-3 sm:px-4 py-2 rounded-lg text-sm font-medium hover:bg-purple-700 transition-colors">
            <Plus size={16} />
            <span className="hidden sm:inline">New Department</span>
            <span className="sm:hidden">New Dept</span>
          </button>
          )}
        </div>

        {/* Two-panel layout */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden" style={{ minHeight: 520 }}>
          <div className="flex h-full">

            {/* ── Left: Departments ── */}
            <div className={`
              flex-shrink-0 border-r border-gray-100 flex flex-col
              w-full sm:w-72 md:w-80
              ${mobileView === 'teams' ? 'hidden sm:flex' : 'flex'}
            `}>
              <div className="p-3 border-b border-gray-100">
                <div className="relative">
                  <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input type="text" placeholder="Search departments..." value={search}
                    onChange={e => setSearch(e.target.value)}
                    className="w-full pl-8 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-400" />
                </div>
              </div>
              <div className="px-4 py-2 border-b border-gray-100">
                <span className="text-xs font-bold uppercase tracking-widest text-gray-400">
                  DEPARTMENTS ({filteredDepts.length})
                </span>
              </div>
              <div className="flex-1 overflow-y-auto">
                {deptsError ? (
                  <div className="p-4"><ErrorBanner message={deptsError} onRetry={loadDepartments} /></div>
                ) : loadingDepts ? (
                  <><SkeletonRow /><SkeletonRow /><SkeletonRow /></>
                ) : filteredDepts.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-40 text-center px-4">
                    <Building2 size={28} className="text-gray-200 mb-2" />
                    <p className="text-sm text-gray-400">
                      {search ? 'No departments match your search.' : 'No departments yet. Create one!'}
                    </p>
                  </div>
                ) : (
                  filteredDepts.map(dept => (
                    <div key={dept.id}
                      onClick={() => { setSelectedDeptId(dept.id); setMobileView('teams'); }}
                      className={`p-4 border-b border-gray-50 cursor-pointer transition-colors ${selectedDeptId === dept.id
                          ? 'bg-purple-50 border-l-4 border-l-purple-500'
                          : 'hover:bg-gray-50 border-l-4 border-l-transparent'
                        }`}>
                      <div className="flex items-center justify-between gap-2 mb-1">
                        {/* FIX 1 & 5: tooltip on dept name for long names */}
                        <span
                          className="font-semibold text-gray-900 text-sm truncate flex-1"
                          title={dept.name}
                        >
                          {dept.name}
                        </span>
                        <div className="flex items-center gap-1 flex-shrink-0">
                          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${dept.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
                            }`}>{dept.isActive ? 'Active' : 'Inactive'}</span>
                          <ChevronRight size={14} className="text-gray-400" />
                        </div>
                      </div>
                      <div className="flex items-center gap-3 text-xs text-gray-400 mb-2">
                        <span className="flex items-center gap-1"><Building2 size={11} /> {dept.teamCount || 0} teams</span>
                        <span className="flex items-center gap-1"><Users size={11} /> {dept.learnerCount || 0} learners</span>
                      </div>
                      {/* FIX 5: description tooltip on hover showing full text */}
                      {dept.description && (
                        <p
                          className="text-xs text-gray-400 truncate mb-2 cursor-default"
                          title={dept.description}
                        >
                          {dept.description}
                        </p>
                      )}
                      <div className="flex items-center gap-2" onClick={e => e.stopPropagation()}>
                        <button
                          onClick={() => setDeptModal({ mode: 'edit', dept })}
                          className="text-xs text-gray-600 border border-gray-300 rounded px-2 py-0.5 hover:bg-gray-50 transition-colors"
                        >
                          Edit
                        </button>
                        <button onClick={e => handleToggleDept(dept, e)}
                          className={`text-xs border rounded px-2 py-0.5 transition-colors ${dept.isActive
                              ? 'text-red-500 border-red-300 hover:bg-red-50'
                              : 'text-green-600 border-green-300 hover:bg-green-50'
                            }`}>
                          {dept.isActive ? 'Deactivate' : 'Activate'}
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>

            <div className={`
              flex-1 flex flex-col min-w-0
              ${mobileView === 'departments' ? 'hidden sm:flex' : 'flex'}
            `}>
              {/* Mobile back */}
              <div className="sm:hidden flex items-center px-4 py-3 border-b border-gray-100 bg-gray-50">
                <button onClick={() => setMobileView('departments')}
                  className="flex items-center gap-1 text-sm text-purple-600 font-medium">
                  <ChevronLeft size={16} /> Departments
                </button>
              </div>

              {selectedDeptId == null ? (
                <div className="flex-1 flex flex-col items-center justify-center text-center p-10">
                  <ChevronRight size={40} className="text-gray-200 mb-3" />
                  <p className="text-gray-500 font-medium">Select a department</p>
                  <p className="text-gray-400 text-sm mt-1">Choose a department to view and manage its teams.</p>
                </div>
              ) : (
                <>
                  <div className="flex items-start justify-between px-4 sm:px-6 pt-4 sm:pt-5 pb-4 border-b border-gray-100">
                    <div className="min-w-0 flex-1 mr-3">
                      <h2
                        className="text-base sm:text-lg font-semibold text-gray-900 truncate"
                        title={selectedDept?.name}
                      >
                        {selectedDept?.name}
                      </h2>
                      {selectedDept?.description && (
                        <p
                          className="text-sm text-gray-500 mt-0.5 line-clamp-1 cursor-default"
                          title={selectedDept.description}
                        >
                          {selectedDept.description}
                        </p>
                      )}
                    </div>
                    <button onClick={() => setTeamModal({ mode: 'create' })}
                      className="flex items-center gap-1.5 bg-purple-600 text-white px-3 sm:px-4 py-2 rounded-lg text-sm font-medium hover:bg-purple-700 flex-shrink-0">
                      <Plus size={15} /> New Team
                    </button>
                  </div>

                  {/* Teams */}
                  <div className="flex-1 overflow-y-auto">
                    {teamsError ? (
                      <div className="p-4"><ErrorBanner message={teamsError} onRetry={() => loadTeams(selectedDeptId)} /></div>
                    ) : loadingTeams ? (
                      <div className="p-4 space-y-3">
                        {[1, 2, 3].map(i => (
                          <div key={i} className="h-14 bg-gray-100 rounded-lg animate-pulse" />
                        ))}
                      </div>
                    ) : teams.length === 0 ? (
                      <div className="flex flex-col items-center justify-center h-full text-center py-16">
                        <div className="w-14 h-14 rounded-full bg-gray-100 flex items-center justify-center mb-3">
                          <UserX size={26} className="text-gray-400" />
                        </div>
                        <p className="font-medium text-gray-700">No teams yet</p>
                        <p className="text-sm text-gray-400 mt-1 max-w-xs">
                          Create a team within <span className="font-medium">{selectedDept?.name}</span> to group learners.
                        </p>
                      </div>
                    ) : (

                      <div>
                        <table className="w-full text-sm min-w-[460px]">
                          <thead className="bg-gray-50 border-b border-gray-100">
                            <tr>
                              <th className="text-left px-4 sm:px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Team Name</th>
                              <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide w-24">
                                <span className="flex items-center gap-1">
                                  <Users size={12} className="text-gray-400" />
                                  Learners
                                </span>
                              </th>
                              <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide w-24">Status</th>
                              <th className="text-right px-4 sm:px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide w-32">Actions</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-50">
                            {teams.map(team => (
                              <tr key={team.id} className="hover:bg-gray-50/60 transition-colors">
                                <td className="px-4 sm:px-6 py-3.5">

                                  <span
                                    className="font-medium text-gray-900 truncate block max-w-[200px]"
                                    title={team.name}
                                  >
                                    {team.name}
                                  </span>
                                </td>

                                <td className="px-4 py-3.5 text-gray-500">
                                  <div className="flex items-center gap-1">
                                    <Users size={12} className="text-gray-400" />
                                    <span>{team.memberCount || 0}</span>
                                  </div>
                                </td>   
                                <td className="px-4 py-3.5">
                                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${team.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
                                    }`}>{team.isActive ? 'Active' : 'Inactive'}</span>
                                </td>
                                <td className="px-4 sm:px-6 py-3.5">
                                  <div className="flex items-center justify-end gap-2">
                                    {/* View More */}
                                    <button onClick={() => setViewDrawer(team)}
                                      className="text-xs text-purple-600 hover:text-purple-800 font-medium whitespace-nowrap">
                                      View More
                                    </button>
                                    {/* Add Learners */}
                                    <button onClick={() => setAddModal(team)}
                                      className="w-7 h-7 flex items-center justify-center rounded-lg bg-purple-50 text-purple-600 hover:bg-purple-100 transition-colors"
                                      title="Add Learners">
                                      <UserPlus size={14} />
                                    </button>
                                    <TeamMenu
                                      team={team}
                                      onEdit={() => setTeamModal({ mode: 'edit', team })}
                                      onToggle={() => handleToggleTeam(team)}
                                    />
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Modals */}
      {deptModal && (
        <DeptModal mode={deptModal.mode} initial={deptModal.dept}  departments={departments}
          onClose={() => setDeptModal(null)} onSave={handleDeptSave} />
      )}
      {teamModal && (
        <TeamModal mode={teamModal.mode} initial={teamModal.team} teams={teams}
          onClose={() => setTeamModal(null)} onSave={handleTeamSave} />
      )}
      {viewDrawer && (
        <ViewTeamDrawer
          team={viewDrawer}
          deptName={selectedDept?.name || ''}
          onClose={() => setViewDrawer(null)}
          onMemberRemoved={handleMemberRemoved}
        />
      )}
      {addModal && (
        <AddLearnersModal team={addModal}
          onClose={() => setAddModal(null)} onAdded={handleLearnersAdded} />
      )}
    </Page>
  );
}